// SignalTestMonitor.jsx - COMPLETE CODE WITH DATE-TIME FORMAT

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  Circle as DotIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const SignalTestMonitor = ({ apiBaseUrl = window.location.origin }) => {
  const [signals, setSignals] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [wsStatus, setWsStatus] = useState('Connecting...');
  const [loading, setLoading] = useState(true);
  const [historicalLoaded, setHistoricalLoaded] = useState(false);

  // Format timestamp as "2025-11-04 08:00:00"
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    
    // Force UTC display by using getUTC* methods
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

  // Load historical signals first
  const loadHistoricalSignals = useCallback(async () => {
    try {
        setLoading(true);
        console.log('[TEST MONITOR] 📚 Loading historical signals...');

        // Load up to 40000 signals (TradingView max limit)
        const response = await fetch(
        `${apiBaseUrl}/strategies/api/test-signal-45s/?limit=40000`
        );

        if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.signals) {
        console.log(`[TEST MONITOR] ✅ Loaded ${data.signals.length} historical signals`);
        
        const transformedSignals = data.signals.map((sig) => ({
            id: `hist-${sig.id}`,
            signal: sig.signal,
            asset: sig.asset,
            price: sig.price,
            timeframe: sig.timeframe,
            trading_hour: sig.trading_hour,
            source: sig.source,
            timestamp: sig.timestamp,
            isHistorical: true,
        }));

        setSignals(transformedSignals);
        setHistoricalLoaded(true);
        }
    } catch (error) {
        console.error('[TEST MONITOR] ❌ Failed to load historical signals:', error);
    } finally {
        setLoading(false);
    }
    }, [apiBaseUrl]);

  // Load historical signals on mount
  useEffect(() => {
    loadHistoricalSignals();
  }, [loadHistoricalSignals]);

  // Connect to WebSocket AFTER historical data is loaded
  useEffect(() => {
    if (!historicalLoaded) {
      console.log('[TEST MONITOR] ⏳ Waiting for historical data before connecting WebSocket...');
      return;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const hostname = apiBaseUrl.split('/')[1];
    const wsUrl = `${wsProtocol}//${hostname}/ws/strategy-alerts/`;

    console.log('[TEST MONITOR] 🔌 Connecting to WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('[TEST MONITOR] ✅ WebSocket Connected - Now listening for live signals');
      setIsConnected(true);
      setWsStatus('Connected - Live Updates Active');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[TEST MONITOR] 📡 WebSocket message:', data);

        if (data.type === 'connection') {
          console.log('[TEST MONITOR] ✅ Connection confirmed');
        } else if (data.type === 'signal_update') {
          const signalData = data.data || {};
          const newSignal = {
            id: `live-${Date.now()}`,
            signal: signalData.signal,
            asset: signalData.asset,
            price: signalData.price,
            timeframe: signalData.timeframe,
            trading_hour: signalData.trading_hour,
            source: signalData.source,
            timestamp: data.timestamp || new Date().toISOString(),
            isHistorical: false,
          };

          console.log('[TEST MONITOR] 🎯 NEW LIVE SIGNAL:', newSignal);

          // Add new signal to the TOP of the list
          setSignals((prev) => [newSignal, ...prev].slice(0, 100));
        }
      } catch (error) {
        console.error('[TEST MONITOR] ❌ Error parsing message:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('[TEST MONITOR] ❌ WebSocket error:', error);
      setWsStatus('Error');
      setIsConnected(false);
    };

    ws.onclose = () => {
      console.log('[TEST MONITOR] ❌ WebSocket disconnected');
      setIsConnected(false);
      setWsStatus('Disconnected - Reconnecting...');
      
      // Auto-reconnect after 3 seconds
      setTimeout(() => {
        if (historicalLoaded) {
          console.log('[TEST MONITOR] 🔄 Reconnecting...');
        }
      }, 3000);
    };

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [apiBaseUrl, historicalLoaded]);

  const getSignalColor = (signal) => {
    if (!signal) return '#00d4ff';
    if (signal.includes('LONG')) return '#00ff88';
    if (signal.includes('SHORT')) return '#ff4757';
    return '#00d4ff';
  };

  const getSignalIcon = (signal) => {
    if (!signal) return <DotIcon />;
    if (signal.includes('LONG')) return <TrendingUpIcon />;
    if (signal.includes('SHORT')) return <TrendingDownIcon />;
    return <DotIcon />;
  };

  const liveSignalsCount = signals.filter(s => !s.isHistorical).length;
  const historicalSignalsCount = signals.filter(s => s.isHistorical).length;

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#0f1419' }}>
      {/* Header */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          bgcolor: alpha('#1a1f2e', 0.8),
          border: `1px solid ${alpha('#00d4ff', 0.2)}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
            🧪 Signal Test Monitor
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Chip
              icon={<DotIcon sx={{ fontSize: 12 }} />}
              label={isConnected ? 'LIVE' : 'OFFLINE'}
              sx={{
                bgcolor: alpha(isConnected ? '#00ff88' : '#ff4757', 0.15),
                color: isConnected ? '#00ff88' : '#ff4757',
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${liveSignalsCount} Live`}
              sx={{
                bgcolor: alpha('#00ff88', 0.15),
                color: '#00ff88',
                fontWeight: 600,
              }}
            />
            <Chip
              label={`${historicalSignalsCount} Historical`}
              sx={{
                bgcolor: alpha('#764ba2', 0.15),
                color: '#764ba2',
                fontWeight: 600,
              }}
            />
            <Chip
              icon={<RefreshIcon sx={{ fontSize: 14 }} />}
              label="Reload"
              onClick={loadHistoricalSignals}
              sx={{
                bgcolor: alpha('#00d4ff', 0.15),
                color: '#00d4ff',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { bgcolor: alpha('#00d4ff', 0.25) }
              }}
            />
          </Stack>
        </Stack>
        <Typography variant="body2" sx={{ color: alpha('#fff', 0.4), mt: 1 }}>
          Status: {wsStatus} | Historical: {historicalLoaded ? '✅ Loaded' : '⏳ Loading...'}
        </Typography>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress sx={{ color: '#00d4ff' }} />
            <Typography sx={{ color: alpha('#fff', 0.5) }}>
              Loading historical signals...
            </Typography>
          </Stack>
        </Box>
      )}

      {/* Signals Table */}
      {!loading && (
        <TableContainer
          component={Paper}
          sx={{
            bgcolor: alpha('#1a1f2e', 0.8),
            border: `1px solid ${alpha('#00d4ff', 0.2)}`,
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#00d4ff', fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ color: '#00d4ff', fontWeight: 700 }}>Date & Time</TableCell>
                <TableCell sx={{ color: '#00d4ff', fontWeight: 700 }}>Signal</TableCell>
                <TableCell sx={{ color: '#00d4ff', fontWeight: 700 }}>Asset</TableCell>
                <TableCell sx={{ color: '#00d4ff', fontWeight: 700 }}>Price</TableCell>
                <TableCell sx={{ color: '#00d4ff', fontWeight: 700 }}>Timeframe</TableCell>
                <TableCell sx={{ color: '#00d4ff', fontWeight: 700 }}>Session</TableCell>
                <TableCell sx={{ color: '#00d4ff', fontWeight: 700 }}>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: alpha('#fff', 0.3), py: 4 }}>
                    {historicalLoaded
                      ? 'No historical signals found. Send a test signal via Postman to see it appear here!'
                      : 'Loading...'}
                  </TableCell>
                </TableRow>
              ) : (
                signals.map((signal) => (
                  <TableRow
                    key={signal.id}
                    sx={{
                      '&:hover': { bgcolor: alpha('#00d4ff', 0.05) },
                      borderLeft: `3px solid ${getSignalColor(signal.signal)}`,
                      bgcolor: signal.isHistorical ? 'transparent' : alpha('#00ff88', 0.03),
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={signal.isHistorical ? 'HIST' : 'LIVE'}
                        size="small"
                        sx={{
                          bgcolor: signal.isHistorical 
                            ? alpha('#764ba2', 0.15) 
                            : alpha('#00ff88', 0.15),
                          color: signal.isHistorical ? '#764ba2' : '#00ff88',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        color: alpha('#00d4ff', 0.9), 
                        fontFamily: 'monospace', 
                        fontSize: '0.85rem',
                        fontWeight: 500
                      }}
                    >
                      {formatTimestamp(signal.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getSignalIcon(signal.signal)}
                        label={signal.signal}
                        size="small"
                        sx={{
                          bgcolor: alpha(getSignalColor(signal.signal), 0.15),
                          color: getSignalColor(signal.signal),
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 600 }}>{signal.asset}</TableCell>
                    <TableCell sx={{ color: '#ffd700', fontFamily: 'monospace' }}>
                      ${signal.price?.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ color: alpha('#fff', 0.7) }}>{signal.timeframe}</TableCell>
                    <TableCell sx={{ color: alpha('#fff', 0.7) }}>{signal.trading_hour}</TableCell>
                    <TableCell>
                      <Chip
                        label={signal.source || 'Unknown'}
                        size="small"
                        sx={{
                          bgcolor: alpha('#764ba2', 0.15),
                          color: '#764ba2',
                          fontSize: '0.7rem',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Instructions */}
      <Paper
        sx={{
          p: 2,
          mt: 3,
          bgcolor: alpha('#1a1f2e', 0.6),
          border: `1px dashed ${alpha('#00d4ff', 0.2)}`,
        }}
      >
        <Typography variant="body2" sx={{ color: alpha('#fff', 0.7), mb: 1 }}>
          📤 <strong>Send test signal via Postman:</strong>
        </Typography>
        <Box
          component="pre"
          sx={{
            bgcolor: alpha('#000', 0.3),
            p: 2,
            borderRadius: 1,
            overflow: 'auto',
            fontSize: '0.85rem',
            color: '#00ff88',
          }}
        >
          {`POST //strategies/api/test-signal-45s/

            {
            "signal": "SMART_BAND_12",
            "timeframe": "45s",
            "price": 25775.00,
            "asset": "NQ",
            "trading_hour": "RTH"
            }`
        }
        </Box>
      </Paper>
    </Box>
  );
};

export default SignalTestMonitor;
